import type {
  ContractParticipantParty,
  ContractParticipantUserGroup,
  GroupRelationGroup,
  UserBusiness,
  UserDetail,
} from '@paid-ui/types';

import { isEmpty, sortBy } from 'lodash';

import { defaultAddress, PartyRole } from '@paid-ui/constants';
import { ContractRole, ContractState, ContractType } from '@paid-ui/enums/contract';
import { PartyLinkingStatus, PayType } from '@paid-ui/enums/party';
import { ContractAction } from '@paid-ui/enums/requiring-action';
import { UserGroupRelation, UserGroupType } from '@paid-ui/enums/user';
import { isInvitedBy } from '@paid-ui/utils/party';

const unspecifiedPartyName = 'Unspecified party';

/**
 * Return the trustee business linked to a trust group, sourced from
 * `business.linkedBusinesses` (PWA-13410).
 */
export const getLinkedTrusteeBusinessFromContractParty = (
  userGroup?: ContractParticipantUserGroup | null,
): ContractParticipantUserGroup['business'] | undefined => {
  return userGroup?.business?.linkedBusinesses?.find(
    (lb) => lb.relation === UserGroupRelation.TRUSTEE,
  )?.business;
};

/**
 * Same as {@link getLinkedTrusteeBusinessFromContractParty} but for the profile group shape
 * (`GroupRelationGroup.business.linkedBusinesses`).
 */
export const getLinkedTrusteeBusinessFromGroup = (
  group?: GroupRelationGroup | null,
): NonNullable<UserBusiness['linkedBusinesses']>[number]['business'] | undefined => {
  return group?.business?.linkedBusinesses?.find((lb) => lb.relation === UserGroupRelation.TRUSTEE)
    ?.business;
};

export function checkIfPartyNotJoined(party: ContractParticipantParty) {
  return (
    party.linkingStatus === PartyLinkingStatus.UNLINKED ||
    party.linkingStatus === PartyLinkingStatus.LINKED
  );
}

export function checkIfPartyJoined(party: ContractParticipantParty) {
  return (
    party.linkingStatus !== PartyLinkingStatus.UNLINKED &&
    party.linkingStatus !== PartyLinkingStatus.LINKED
  );
}

/**
 * Changing the primary contact hands a party's representation to another member of its user
 * group, which only applies to a business party — an individuals party is the person
 * themselves, so there is nobody to hand it to (PWA-16173). The party type is what rules that
 * out: a joint-individuals party has more than one user in its group, so group size never
 * distinguished the two.
 *
 * Group size is not a precondition either. The flyout can create a brand-new member of the
 * party's own group and nominate them, so a one-person business party still has somewhere to
 * hand its representation to. The group itself must resolve, though — everything the flyout
 * does keys off `userGroup.id`, and without one it can neither list members nor add a new
 * primary contact, so the action would open a dead end.
 *
 * An unset `linkingStatus` counts as not joined, so a party that has not been resolved yet
 * cannot change its primary contact.
 *
 * @param party - the participant party row being rendered
 * @param canChangePrimaryContact - contract-level permission from `contractManager.permissions`
 */
export function checkIfPartyCanChangePrimaryContact(
  party: ContractParticipantParty,
  canChangePrimaryContact: boolean,
) {
  return (
    party.isCurrentParty &&
    canChangePrimaryContact &&
    party.type !== UserGroupType.INDIVIDUALS &&
    !!party.linkingStatus &&
    checkIfPartyJoined(party) &&
    !!party.userGroup?.id
  );
}

/**
 * Linking statuses a joined party can still be replaced from. A party further along than these
 * is in the signing queue, which contract service refuses to unjoin.
 */
const replaceableJoinedStatuses = new Set([
  PartyLinkingStatus.JOINED,
  PartyLinkingStatus.FILLED_INFO,
  PartyLinkingStatus.READY_TO_SIGN,
]);

/** Contract states where the parties are still negotiating, so a party may still be swapped out. */
const negotiatingStates = new Set<string>([
  ContractState.SUBMITTED,
  ContractState.RESUBMITTED,
  ContractState.REJECTED,
]);

/**
 * Whether the signed-in user may put someone else on a party that has already joined
 * (PWA-16217). Contract service unjoins the party as part of the re-invite, which takes its
 * user group off the contract and sends the contract back for review, so it only allows it
 * for the party's own inviter and only before the contract is accepted.
 *
 * A party that has not joined yet is not covered here — that is the plain "edit / resend
 * invite" action. Its email re-invite follows {@link canReinviteParty}: only the inviter for a
 * contractual party, anyone managing the contract for a non-contractual one. Anyone who can manage
 * the contract may still share an invite link.
 *
 * @param party - the participant party row being rendered
 * @param currentParty - the signed-in user's own party on this contract
 * @param contractState - the state of the contract being viewed
 */
export function checkIfJoinedPartyCanBeReplaced(
  party: ContractParticipantParty,
  currentParty: ContractParticipantParty | null | undefined,
  contractState: ContractState | '',
) {
  return (
    !party.isCurrentParty &&
    party.invitationType === PartyRole.INVITEE &&
    isInvitedBy(party, currentParty) &&
    replaceableJoinedStatuses.has(party.linkingStatus) &&
    negotiatingStates.has(contractState)
  );
}

/**
 * Get all participants' formatted to display for contract select
 *
 * @param participantParties - participant parties
 * @param profile - User profile
 * @returns Formatted to displays
 */
export function getPartiesNameToDisplay(
  participantParties?: ContractParticipantParty[],
  profile?: UserDetail,
) {
  if (!profile || !participantParties || participantParties.length === 0) {
    return unspecifiedPartyName;
  }

  const targetParticipant = participantParties.find(
    (party) => profile?.groupRelation?.group.id === party?.userGroup?.id,
  );
  const hasBusiness = !isEmpty(targetParticipant?.businessContractInfo);
  const firstUser = targetParticipant?.usersInfo?.find(
    (user) => user.id === targetParticipant?.nominee?.id,
  );
  const usersLength = targetParticipant?.usersInfo?.length ?? 1;
  const userName =
    usersLength > 1
      ? [(firstUser?.firstName, firstUser?.lastName)].join('')
      : [firstUser?.firstName, firstUser?.lastName, ` + ${usersLength - 1} other`].join('');

  switch (targetParticipant?.payType) {
    case PayType.PAYER: {
      return hasBusiness ? targetParticipant.displayName : userName;
    }

    case PayType.PAYEE: {
      return targetParticipant.displayName ?? userName;
    }

    default: {
      return unspecifiedPartyName;
    }
  }
}

/**
 * Format contract party to invitee
 *
 * @param party - Contract party
 * @returns Formatted invitee
 */
export function formatPartyToInvitee(party?: ContractParticipantParty | null) {
  const partyInfo = {
    firstName: party?.nominee?.firstName ?? '',
    lastName: party?.nominee?.lastName ?? '',
    email: party?.nominee?.email ?? '',
    confirmEmail: party?.nominee?.email ?? '',
  };
  if (!party) {
    return {
      businessId: '',
      businessName: '',
      type: PayType.PAYEE,
      address: defaultAddress,
      partyInfo,
    };
  }

  const business = party.userGroup?.business;

  if (business) {
    return {
      businessId: business?.id ?? '',
      businessName: business?.registeredName ?? '',
      type: party.payType ?? PayType.PAYEE,
      address: business?.registeredAddress ?? defaultAddress,
      partyInfo,
      displayName: party?.displayName,
    };
  }

  return {
    businessId: '',
    businessName: '',
    type: PayType.PAYEE,
    address: defaultAddress,
    partyInfo,
  };
}

/**
 * Get first inviteeParty from contract participant parties.
 *
 * @param parties - All contract participant parties
 * @returns First invitee or null
 */
export function getInviteeParty(parties: ContractParticipantParty[] = []) {
  return (
    parties.find(
      (participant) =>
        participant.invitationType === PartyRole.INVITEE &&
        [PayType.PAYEE, PayType.PAYER, PayType.INTERMEDIARY].includes(participant.payType),
    ) ?? null
  );
}

/**
 * Get first inviterParty from contract participant parties.
 *
 * @param parties All contract participant parties
 * @returns First invitee or null
 */
export function getInviterParty(parties: ContractParticipantParty[] = []) {
  return (
    parties.find(
      (participant) =>
        participant.invitationType === PartyRole.INVITER &&
        [PayType.PAYEE, PayType.PAYER, PayType.INTERMEDIARY].includes(participant.payType),
    ) ?? null
  );
}

/**
 * Get payee party from contract participants.
 *
 * @param participants All contract participants
 * @returns First payer or null
 */
export function getPayeeParty(participants: ContractParticipantParty[] = []) {
  return participants.find((participant) => participant.payType === PayType.PAYEE) ?? null;
}

/**
 * Get payer party from contract participants.
 *
 * @param participants All contract participants
 * @returns First payer or null
 */
export function getPayerParty(participants: ContractParticipantParty[] = []) {
  return participants.find((participant) => participant.payType === PayType.PAYER) ?? null;
}

/**
 * Get first party from contract participants.
 *
 * @param participants All contract participants
 * @param role Target contract role
 * @returns Found party or null
 */
export function getPartyByRole(participants: ContractParticipantParty[] = [], role: ContractRole) {
  return participants.find((participant) => participant.role?.role === role) ?? null;
}

/**
 * Get parties from contract participants.
 *
 * @param participants All contract participants
 * @param role Target contract role
 * @returns Found party or null
 */
export function getPartiesByRole(
  participants: ContractParticipantParty[] = [],
  role: ContractRole,
) {
  return participants.filter((participant) => participant.role?.role === role) ?? [];
}

/**
 * Get two contract parties based on participants data
 *
 * @param parties - All participant parties
 * @returns Current party and the other party
 */
export const getContractParticipantParties = (parties: ContractParticipantParty[] = []) => {
  const currentParty = parties.find((party) => party.isCurrentParty);

  if (!currentParty)
    return {
      theParty: null,
      currentParty: null,
      theOtherParty: null,
    };

  const currentPayType = currentParty.payType;
  const payeeParty = parties.find((party) => party.payType === PayType.PAYEE) ?? null;
  const payerParty = parties.find((party) => party.payType === PayType.PAYER) ?? null;
  const reviewerParty =
    parties.find(
      (party) =>
        party.payType !== PayType.PAYEE &&
        party.role?.actions.includes(ContractAction.CONTRACT_REVIEW),
    ) ?? payerParty;

  return {
    theParty: currentParty,
    // Alias to the party
    currentParty,
    theOtherParty: currentPayType === PayType.PAYEE ? reviewerParty : payeeParty,
  };
};

/**
 * Get last reviewer in contract participant parties.
 *
 * @param participantParties - All participant parties
 * @returns Last reviewer participant
 */
export function getLastReviewParty(participantParties: ContractParticipantParty[]) {
  if (participantParties.some((party) => !!party.snapshotStatus?.snapshotVersion)) {
    return (
      sortBy(participantParties, (party) => {
        return party.snapshotStatus?.snapshotVersion ? -party.snapshotStatus.snapshotVersion : 0;
      }).at(0) ?? null
    );
  }
  return null;
}

/**
 * Get contract role based on contract type and party.
 *
 * @param contractType Contract type
 * @param party Contract Party
 * @returns Contract role
 */
export function getPartyRole(contractType: ContractType, party: ContractParticipantParty | null) {
  if (!party) {
    return ContractRole.BUILDER;
  }
  if (contractType === ContractType.SUBCONTRACT) {
    if (party.payType === PayType.PAYER) {
      return ContractRole.BUILDER;
    }
    return ContractRole.SUBCONTRACTOR;
  }
  if (contractType === ContractType.SUPPLY_CONTRACT) {
    if (party.payType === PayType.PAYER) {
      return ContractRole.BUILDER;
    }
    return ContractRole.SUPPLIER;
  }
  if (party.payType === PayType.PAYER) {
    return ContractRole.PRINCIPAL;
  }
  if (party.payType === PayType.INTERMEDIARY) {
    return ContractRole.SUPERINTENDENT;
  }
  return ContractRole.BUILDER;
}

/**
 * Get the other claim/variation review party from contract participants.
 *
 * @param participants All contract participants
 * @returns The other claim review party or null
 */
export function getTheOtherClaimReviewParty(participants: ContractParticipantParty[] = []) {
  return (
    participants.find(
      (participant) =>
        participant.role?.actions.includes(ContractAction.CLAIM_REVIEW) ||
        participant.role?.actions.includes(ContractAction.CLAIM_ASSESS),
    ) ?? null
  );
}

const inviteeNameFormatter = new Intl.ListFormat('en', { type: 'conjunction' });

/**
 * Tooltip body for the "Inviter" badge — lists every invitee party other than
 * the signed-in user. Falls back to a shorter sentence when there are none.
 */
export function buildInviterTooltip(
  participants: readonly ContractParticipantParty[] = [],
): string {
  const otherInviteeNames = participants
    .filter((p) => p.invitationType === PartyRole.INVITEE && !p.isCurrentParty)
    .map((p) => p.displayName || unspecifiedPartyName);

  if (otherInviteeNames.length === 0) {
    return 'This person invited you to join this contract.';
  }
  return `This person invited you and ${inviteeNameFormatter.format(otherInviteeNames)} to join this contract.`;
}
